import { isApiError } from '@multaqa/api-client'
import { useRef } from 'react'

/**
 * One `Idempotency-Key` per user intent, as Stripe's API expects: a retry of
 * the same request reuses the key, so a reply lost on the way back cannot
 * create a second order; the next intent gets a new key.
 *
 * The intent is the mutation's variables. The typed client throws an
 * `ApiError` for every failed answer (status 0 when nothing came back). The
 * key is kept after status 0 or a 5xx, the cases where the server may have
 * acted; it is dropped after a success or a refusal (4xx), because the next
 * attempt is a new action, usually with changed data.
 */
export function useIdempotentSend() {
  const last = useRef<{ intent: string; key: string } | null>(null)

  return async <T>(intent: unknown, send: (key: string) => Promise<T>): Promise<T> => {
    const id = JSON.stringify(intent ?? null)
    if (last.current?.intent !== id) last.current = { intent: id, key: crypto.randomUUID() }

    try {
      const result = await send(last.current.key)
      last.current = null
      return result
    } catch (error) {
      if (isApiError(error) && error.status >= 400 && error.status < 500) last.current = null
      throw error
    }
  }
}
